import React, { useState } from 'react'
import { Modal } from '../../../UI/Modal'
import { DoorsTableTabs } from '../../../Cards/DoorSections/DoorsTableTabs'
import { TabbedDoorSectionProps } from '../../../Cards/DoorSections/types'
import { PlacesSummary } from 'models/summaries'
import { formatInteger } from 'utils/formatting'

interface DoorsModalProps {
  show: boolean
  onHide: () => void
  chainData?: TabbedDoorSectionProps['chainData']
  chainID?: number
  forChainProxyId?: number
  chainName?: string
  subCategory: {
    name: string
    slug: string
    description: string
  }
  matchedTableComponent: TabbedDoorSectionProps['matchedTableComponent']
  matchedTableProps?: TabbedDoorSectionProps['matchedTableProps']
  matchedTableKey?: string
  unmatchedTableKeyPrefix: string
  className?: string
}

export function DoorsModal({
  show,
  onHide,
  chainData,
  chainID,
  forChainProxyId,
  chainName,
  subCategory,
  matchedTableComponent,
  matchedTableProps = {},
  matchedTableKey,
  unmatchedTableKeyPrefix,
  className = '',
}: DoorsModalProps) {
  // FIR-7760: the matched table reports its real row count here (via
  // setDoorsSummary), so the header "(N locations)" and the "First Bite (N)"
  // tab badge both reflect what the table actually lists instead of the
  // denormalized chainData.count, which could disagree with the rows. Seeded
  // with chainData.count so it shows immediately and matched tables that don't
  // report a summary (non-restaurant verticals) keep that value.
  const [matchedSummary, setMatchedSummary] = useState<PlacesSummary>({
    places_count: chainData?.count ?? 0,
    chains_count: 0,
    total_ltv: 0,
    isLoading: false,
  })

  const matchedDoorCount = matchedSummary.isLoading
    ? (chainData?.count ?? 0)
    : (matchedSummary.places_count ?? chainData?.count ?? 0)

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-bottom-0 !px-8 !pt-6">
        <Modal.Title>
          {chainName || chainData?.name} Doors
          {chainData?.count != null && (
            <span className="text-muted font-light ml-2">
              ({formatInteger(matchedDoorCount)} locations)
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="!px-8 !pb-6">
        <DoorsTableTabs
          forChainProxyId={forChainProxyId}
          chainID={chainID}
          chainData={chainData}
          subCategory={subCategory}
          matchedTableComponent={matchedTableComponent}
          matchedTableProps={matchedTableProps}
          matchedTableKey={matchedTableKey}
          unmatchedTableKeyPrefix={unmatchedTableKeyPrefix}
          className={className}
          matchedDoorCount={matchedDoorCount}
          setMatchedSummary={setMatchedSummary}
        />
      </Modal.Body>
    </Modal>
  )
}
